import {
  RECOMMENDATION_KINDS,
  type BuiltinRecommendationKind,
  type Dataset,
  type Expression,
  type ModelingRecommendation,
  type SemanticColumn,
  type SemanticModel,
  type SemanticType,
  type TimeGrain,
} from '@aibi/contracts';
import { fitId, toIdSlug } from '@aibi/shared';
import type { ModelDiagnostic, ModelDiagnosticCode } from '../engine/diagnostics.js';
import type { ColumnInput, ModelOperation, Patch } from '../engine/operations.js';
import { mapExpressionColumns } from '../expression-references.js';
import { compatibleTypes, typeFamily } from '../expression-types.js';
import { resolveRef, type ResolvedRef } from './resolve-ref.js';

export type BuiltinRecommendation = Extract<
  ModelingRecommendation,
  { kind: BuiltinRecommendationKind }
>;
type RecommendationOf<K extends BuiltinRecommendationKind> = Extract<
  BuiltinRecommendation,
  { kind: K }
>;

const BUILTIN_KINDS: ReadonlySet<string> = new Set(RECOMMENDATION_KINDS);
export const isBuiltinRecommendation = (
  recommendation: ModelingRecommendation,
): recommendation is BuiltinRecommendation => BUILTIN_KINDS.has(recommendation.kind);

/**
 * Resultado de traducir una recomendacion:
 * - `operations`: operaciones listas para `applyOperations` (puede ser una lista vacia
 *   si el modelo ya contiene lo propuesto);
 * - `not-applicable`: la recomendacion no afecta al modelo (p. ej. una clave primaria);
 * - `unsupported`: tipo sin traduccion (recomendaciones de plugins);
 * - `invalid`: referencia obsoleta o propuesta incompatible con el modelo actual.
 */
export interface Translation {
  readonly status: 'operations' | 'not-applicable' | 'unsupported' | 'invalid';
  readonly operations: readonly ModelOperation[];
  readonly diagnostics: readonly ModelDiagnostic[];
}

/** Ids deterministas de los objetos que crea la traduccion. */
export const recommendationModelIds = {
  dimension: (columnId: string): string => fitId(`dim.${columnId}`),
  measure: (columnId: string): string => fitId(`msr.${columnId}`),
  calculatedMeasure: (name: string): string => fitId(`cmsr.${toIdSlug(name)}`),
  calculatedDimension: (key: string): string => fitId(`cdim.${key}`),
  relationship: (fromColumnId: string, toColumnId: string): string =>
    fitId(`rel.${fromColumnId}.${toColumnId}`),
  hierarchy: (key: string): string => fitId(`hier.${key}`),
  hierarchyLevel: (hierarchyId: string, position: number): string =>
    fitId(`${hierarchyId}.l${String(position)}`),
} as const;

/** Clave de la recomendacion sin el prefijo `rec.<kind>.` que agrega Discovery. */
export function recommendationKey(recommendation: ModelingRecommendation): string {
  const prefix = `rec.${recommendation.kind}.`;
  if (recommendation.id.startsWith(prefix)) return recommendation.id.slice(prefix.length);
  return recommendation.id.startsWith('rec.') ? recommendation.id.slice(4) : recommendation.id;
}

const CALENDAR_FUNCTIONS: ReadonlySet<TimeGrain> = new Set([
  'year',
  'quarter',
  'month',
  'week',
  'day',
]);

/**
 * Traduce una recomendacion (normalmente `accepted`) a operaciones sobre el modelo.
 * Funcion pura: no aplica nada ni consulta el `status`; esa politica vive en
 * `applyRecommendations`. Las referencias fisicas se resuelven contra el Dataset.
 *
 * @example
 * const translation = translateRecommendation(rec, model, dataset);
 * if (translation.status === 'operations') applyOperations(model, [...translation.operations], context);
 */
export function translateRecommendation(
  recommendation: ModelingRecommendation,
  model: SemanticModel,
  dataset: Dataset,
): Translation {
  if (!isBuiltinRecommendation(recommendation)) {
    return outcome('unsupported', [
      diagnostic(
        recommendation,
        'unsupported',
        `Las recomendaciones "${recommendation.kind}" no tienen traduccion al modelo.`,
        'warning',
      ),
    ]);
  }
  return new Translator(recommendation, model, dataset).translate();
}

function diagnostic(
  recommendation: ModelingRecommendation,
  code: ModelDiagnosticCode,
  message: string,
  severity: ModelDiagnostic['severity'] = 'error',
  objectId?: string,
): ModelDiagnostic {
  return {
    severity,
    code,
    message,
    recommendationId: recommendation.id,
    ...(objectId === undefined ? {} : { objectId }),
  };
}

function outcome(
  status: Translation['status'],
  diagnostics: readonly ModelDiagnostic[] = [],
  operations: readonly ModelOperation[] = [],
): Translation {
  return { status, operations, diagnostics };
}

class Translator {
  readonly #operations: ModelOperation[] = [];
  readonly #notes: ModelDiagnostic[] = [];
  /** Estado de columnas tras las operaciones ya generadas (para no pisar anotaciones). */
  readonly #columnChanges = new Map<string, Patch<ColumnInput>>();
  readonly #pendingIds = new Set<string>();

  constructor(
    private readonly rec: BuiltinRecommendation,
    private readonly model: SemanticModel,
    private readonly dataset: Dataset,
  ) {}

  translate(): Translation {
    const result = this.#dispatch();
    if (result) return result;
    return outcome('operations', this.#notes, this.#operations);
  }

  #dispatch(): Translation | undefined {
    const { rec } = this;
    switch (rec.kind) {
      case 'dimension':
        return this.#dimension(rec);
      case 'measure':
        return this.#measure(rec);
      case 'calculatedMeasure':
        return this.#calculatedMeasure(rec);
      case 'calculatedDimension':
        return this.#calculatedDimension(rec);
      case 'date':
        return this.#date(rec);
      case 'geography':
        return this.#annotateFirst(rec.proposal.semanticType);
      case 'identifier':
        return this.#annotateFirst('identifier');
      case 'quantity':
        return this.#annotateFirst(
          'quantity',
          true,
          rec.proposal.unit === undefined ? undefined : { unit: rec.proposal.unit },
        );
      case 'currency':
        return this.#currency(rec);
      case 'format':
        return this.#format(rec);
      case 'relationship':
        return this.#relationship(rec);
      case 'hierarchy':
        return this.#hierarchy(rec);
      case 'primaryKey':
      case 'unique':
        return outcome('not-applicable', [
          diagnostic(
            rec,
            'not-applicable',
            'Las claves pertenecen al Dataset (datasetFromDiscovery), no al Semantic Model.',
            'info',
          ),
        ]);
    }
  }

  // ---------------------------------------------------------------------------
  // Utilidades
  // ---------------------------------------------------------------------------

  #invalid(code: ModelDiagnosticCode, message: string, objectId?: string): Translation {
    return outcome('invalid', [diagnostic(this.rec, code, message, 'error', objectId)]);
  }

  #note(message: string, objectId?: string): void {
    this.#notes.push(diagnostic(this.rec, 'skipped', message, 'info', objectId));
  }

  get #provenance(): { sourceRecommendationId: string } {
    return { sourceRecommendationId: this.rec.id };
  }

  #exists(id: string): boolean {
    if (this.#pendingIds.has(id)) return true;
    const { model } = this;
    return [
      ...model.dimensions,
      ...model.calculatedDimensions,
      ...model.measures,
      ...model.calculatedMeasures,
      ...model.relationships,
      ...model.hierarchies,
    ].some((item) => item.id === id);
  }

  #resolve(index: number, requireColumn = true): ResolvedRef | Translation {
    const ref = this.rec.sourceRefs[index];
    if (ref === undefined) {
      return this.#invalid('stale-reference', `La referencia ${String(index)} no existe.`);
    }
    const resolved = resolveRef(ref, this.dataset, this.model, { requireColumn });
    return resolved.ok
      ? resolved.value
      : outcome('invalid', [{ ...resolved.error, recommendationId: this.rec.id }]);
  }

  #resolveColumn(index = 0): { ref: ResolvedRef; column: SemanticColumn } | Translation {
    const ref = this.#resolve(index);
    if ('status' in ref) return ref;
    const { column } = ref;
    return column === undefined
      ? this.#invalid('stale-reference', 'La referencia no apunta a una columna.')
      : { ref, column };
  }

  /** Semantic type vigente de la columna, contando cambios ya generados. */
  #semanticTypeOf(column: SemanticColumn): SemanticType | undefined {
    const pending = this.#columnChanges.get(column.id);
    return pending?.semanticType ?? column.semanticType;
  }

  /**
   * Anota la columna con un semantic type sin sobrescribir el del usuario: si ya tiene
   * otro distinto, la recomendacion es incompatible.
   */
  #annotate(
    column: SemanticColumn,
    semanticType: SemanticType | undefined,
    extra: Patch<ColumnInput> = {},
  ): Translation | undefined {
    const current = this.#semanticTypeOf(column);
    if (semanticType !== undefined && current !== undefined && current !== semanticType) {
      return this.#invalid(
        'incompatible',
        `La columna "${column.id}" ya esta clasificada como ${current}.`,
        column.id,
      );
    }
    const changes: Patch<ColumnInput> = {
      ...(semanticType !== undefined && current === undefined ? { semanticType } : {}),
      ...extra,
    };
    if (Object.keys(changes).length === 0) return undefined;
    this.#columnChanges.set(column.id, { ...this.#columnChanges.get(column.id), ...changes });
    this.#operations.push({ op: 'updateColumn', columnId: column.id, changes });
    return undefined;
  }

  #annotateFirst(
    semanticType: SemanticType,
    numeric = false,
    metadata?: Record<string, string>,
  ): Translation | undefined {
    const resolved = this.#resolveColumn();
    if ('status' in resolved) return resolved;
    const { column } = resolved;
    if (numeric && !['numeric', 'unknown'].includes(typeFamily(column.dataType))) {
      return this.#invalid(
        'incompatible',
        `"${semanticType}" requiere una columna numerica ("${column.id}" es ${column.dataType}).`,
        column.id,
      );
    }
    return this.#annotate(
      column,
      semanticType,
      metadata === undefined ? {} : { metadata: { ...column.metadata, ...metadata } },
    );
  }

  /** Dimension existente sobre la columna, o una nueva con id determinista. */
  #ensureDimension(
    column: SemanticColumn,
    item: {
      name: string;
      label?: string | undefined;
      role: 'categorical' | 'temporal' | 'geographic' | 'identifier';
      defaultTimeGrain?: TimeGrain | undefined;
    },
  ): string {
    const existing = this.model.dimensions.find((dimension) => dimension.columnId === column.id);
    if (existing) {
      this.#note(`La columna ya tiene la dimension "${existing.id}".`, existing.id);
      return existing.id;
    }
    const id = recommendationModelIds.dimension(column.id);
    if (this.#exists(id)) return id;
    this.#pendingIds.add(id);
    this.#operations.push({
      op: 'add',
      collection: 'dimensions',
      item: {
        id,
        name: item.name,
        ...(item.label === undefined ? {} : { label: item.label }),
        columnId: column.id,
        role: item.role,
        ...(item.defaultTimeGrain === undefined ? {} : { defaultTimeGrain: item.defaultTimeGrain }),
        metadata: this.#provenance,
      },
    });
    return id;
  }

  /** Traduce los ids de columna de Discovery de una expresion a ids del modelo. */
  #translateExpression(expression: Expression): Expression | Translation {
    const ids = new Map<string, string>();
    for (const [index, ref] of this.rec.sourceRefs.entries()) {
      if (ref.column === undefined) continue;
      const resolved = this.#resolve(index);
      if ('status' in resolved) return resolved;
      if (resolved.column === undefined) continue;
      ids.set(ref.columnId ?? resolved.column.id, resolved.column.id);
      ids.set(resolved.column.id, resolved.column.id);
    }
    const missing: string[] = [];
    const translated = mapExpressionColumns(expression, (columnId) => {
      const mapped = ids.get(columnId);
      if (mapped === undefined) missing.push(columnId);
      return mapped ?? columnId;
    });
    return missing.length > 0
      ? this.#invalid(
          'stale-reference',
          `La expresion usa columnas sin referencia resoluble: ${missing.join(', ')}.`,
        )
      : translated;
  }

  // ---------------------------------------------------------------------------
  // Traducciones por tipo
  // ---------------------------------------------------------------------------

  #dimension(rec: RecommendationOf<'dimension'>): Translation | undefined {
    const resolved = this.#resolveColumn();
    if ('status' in resolved) return resolved;
    const { column } = resolved;
    const { proposal } = rec;
    if (!compatibleTypes(proposal.dataType, column.dataType)) {
      return this.#invalid(
        'stale-reference',
        `La columna "${column.id}" es ${column.dataType}; la recomendacion esperaba ${proposal.dataType}.`,
        column.id,
      );
    }
    if (
      (proposal.role === 'temporal' || proposal.defaultTimeGrain !== undefined) &&
      typeFamily(column.dataType) !== 'temporal'
    ) {
      return this.#invalid(
        'incompatible',
        `Una dimension temporal requiere una columna de fecha ("${column.id}" es ${column.dataType}).`,
        column.id,
      );
    }
    const annotated = this.#annotate(column, proposal.semanticType);
    if (annotated) return annotated;
    this.#ensureDimension(column, proposal);
    return undefined;
  }

  #measure(rec: RecommendationOf<'measure'>): Translation | undefined {
    const resolved = this.#resolveColumn();
    if ('status' in resolved) return resolved;
    const { ref, column } = resolved;
    const { proposal } = rec;
    if (proposal.dataType !== undefined && !compatibleTypes(proposal.dataType, column.dataType)) {
      return this.#invalid(
        'stale-reference',
        `La columna "${column.id}" es ${column.dataType}; la recomendacion esperaba ${proposal.dataType}.`,
        column.id,
      );
    }
    const annotated = this.#annotate(column, proposal.semanticType);
    if (annotated) return annotated;
    const existing = this.model.measures.find(
      (measure) => measure.columnId === column.id && measure.aggregation === proposal.aggregation,
    );
    if (existing) {
      this.#note(`Ya existe la medida "${existing.id}" con la misma agregacion.`, existing.id);
      return undefined;
    }
    const base = recommendationModelIds.measure(column.id);
    const id = this.#exists(base) ? fitId(`${base}.${proposal.aggregation}`) : base;
    this.#pendingIds.add(id);
    this.#operations.push({
      op: 'add',
      collection: 'measures',
      item: {
        id,
        name: proposal.name,
        ...(proposal.label === undefined ? {} : { label: proposal.label }),
        aggregation: proposal.aggregation,
        columnId: column.id,
        tableId: ref.table.id,
        ...(proposal.format === undefined ? {} : { format: proposal.format }),
        metadata: this.#provenance,
      },
    });
    return undefined;
  }

  #calculatedMeasure(rec: RecommendationOf<'calculatedMeasure'>): Translation | undefined {
    const { proposal } = rec;
    const id = recommendationModelIds.calculatedMeasure(proposal.name);
    if (this.#exists(id)) {
      this.#note(`Ya existe la medida calculada "${id}".`, id);
      return undefined;
    }
    const expression = this.#translateExpression(proposal.expression);
    if ('status' in expression) return expression;
    const home = this.#resolve(0, false);
    this.#operations.push({
      op: 'add',
      collection: 'calculatedMeasures',
      item: {
        id,
        name: proposal.name,
        ...(proposal.label === undefined ? {} : { label: proposal.label }),
        expression,
        ...(proposal.formula === undefined ? {} : { formula: proposal.formula }),
        ...('status' in home ? {} : { tableId: home.table.id }),
        ...(proposal.format === undefined ? {} : { format: proposal.format }),
        metadata: this.#provenance,
      },
    });
    return undefined;
  }

  #calculatedDimension(rec: RecommendationOf<'calculatedDimension'>): Translation | undefined {
    const { proposal } = rec;
    const home = this.#resolve(0, false);
    if ('status' in home) return home;
    const grain = grainOf(proposal.expression);
    const key =
      grain !== undefined && home.column !== undefined
        ? `${home.column.id}.${grain}`
        : toIdSlug(proposal.name);
    const id = recommendationModelIds.calculatedDimension(key);
    if (this.#exists(id)) {
      this.#note(`Ya existe la dimension calculada "${id}".`, id);
      return undefined;
    }
    const expression = this.#translateExpression(proposal.expression);
    if ('status' in expression) return expression;
    this.#operations.push({
      op: 'add',
      collection: 'calculatedDimensions',
      item: {
        id,
        name: proposal.name,
        ...(proposal.label === undefined ? {} : { label: proposal.label }),
        tableId: home.table.id,
        dataType: proposal.dataType,
        role: proposal.role,
        expression,
        ...(proposal.formula === undefined ? {} : { formula: proposal.formula }),
        metadata: this.#provenance,
      },
    });
    return undefined;
  }

  #date(rec: RecommendationOf<'date'>): Translation | undefined {
    const resolved = this.#resolveColumn();
    if ('status' in resolved) return resolved;
    const { column } = resolved;
    const annotated = this.#annotate(column, 'date');
    if (annotated) return annotated;
    if (!rec.proposal.primaryCandidate) return undefined;
    if (this.model.settings.primaryDateDimensionId !== undefined) {
      this.#note('El modelo ya tiene una fecha principal; no se reemplaza.');
      return undefined;
    }
    const dimension = this.model.dimensions.find((item) => item.columnId === column.id);
    if (!dimension || typeFamily(column.dataType) !== 'temporal') {
      this.#note('No hay una dimension de fecha sobre la columna para usarla como principal.');
      return undefined;
    }
    this.#operations.push({
      op: 'updateSettings',
      changes: { primaryDateDimensionId: dimension.id },
    });
    return undefined;
  }

  #currency(rec: RecommendationOf<'currency'>): Translation | undefined {
    const annotated = this.#annotateFirst('currency', true);
    if (annotated) return annotated;
    const { currency } = rec.proposal;
    if (currency !== undefined && this.model.settings.currency === undefined) {
      this.#operations.push({ op: 'updateSettings', changes: { currency } });
    }
    return undefined;
  }

  #format(rec: RecommendationOf<'format'>): Translation | undefined {
    const resolved = this.#resolveColumn();
    if ('status' in resolved) return resolved;
    const { column } = resolved;
    if (column.format !== undefined) {
      this.#note(`La columna "${column.id}" ya tiene formato; no se reemplaza.`, column.id);
      return undefined;
    }
    return this.#annotate(column, undefined, { format: rec.proposal.spec });
  }

  #relationship(rec: RecommendationOf<'relationship'>): Translation | undefined {
    const from = this.#resolveColumn(rec.proposal.fromRefIndex);
    if ('status' in from) return from;
    const to = this.#resolveColumn(rec.proposal.toRefIndex);
    if ('status' in to) return to;
    const fromId = from.column.id;
    const toId = to.column.id;
    const existing = this.model.relationships.find(
      (item) =>
        (item.fromColumnId === fromId && item.toColumnId === toId) ||
        (item.fromColumnId === toId && item.toColumnId === fromId),
    );
    if (existing) {
      this.#note(`Ya existe la relacion "${existing.id}" entre esas columnas.`, existing.id);
      return undefined;
    }
    this.#operations.push({
      op: 'add',
      collection: 'relationships',
      item: {
        id: recommendationModelIds.relationship(fromId, toId),
        fromColumnId: fromId,
        toColumnId: toId,
        cardinality: rec.proposal.cardinality,
        crossFilter: rec.proposal.crossFilter,
      },
    });
    return undefined;
  }

  #hierarchy(rec: RecommendationOf<'hierarchy'>): Translation | undefined {
    const hierarchyId = recommendationModelIds.hierarchy(recommendationKey(rec));
    if (this.#exists(hierarchyId)) {
      this.#note(`Ya existe la jerarquia "${hierarchyId}".`, hierarchyId);
      return undefined;
    }
    const levels: { id: string; name: string; dimensionId: string }[] = [];
    for (const [position, level] of rec.proposal.levels.entries()) {
      const resolved = this.#resolveColumn(level.refIndex);
      if ('status' in resolved) return resolved;
      const { ref, column } = resolved;
      let dimensionId: string;
      if (level.timeGrain !== undefined && typeFamily(column.dataType) === 'temporal') {
        const calendar = this.#calendarLevel(ref, column, level.timeGrain, level.name);
        if (typeof calendar !== 'string') return calendar;
        dimensionId = calendar;
      } else {
        const geographic = this.#semanticTypeOf(column)?.startsWith('geo.') === true;
        dimensionId = this.#ensureDimension(column, {
          name: level.name,
          role: geographic ? 'geographic' : 'categorical',
        });
      }
      levels.push({
        id: recommendationModelIds.hierarchyLevel(hierarchyId, position + 1),
        name: level.name,
        dimensionId,
      });
    }
    this.#pendingIds.add(hierarchyId);
    this.#operations.push({
      op: 'add',
      collection: 'hierarchies',
      item: {
        id: hierarchyId,
        name: rec.proposal.name,
        ...(rec.proposal.label === undefined ? {} : { label: rec.proposal.label }),
        levels,
        metadata: this.#provenance,
      },
    });
    return undefined;
  }

  /** Nivel de calendario: dimension calculada `year()`, `month()`... sobre la fecha. */
  #calendarLevel(
    ref: ResolvedRef,
    column: SemanticColumn,
    grain: TimeGrain,
    levelName: string,
  ): string | Translation {
    const id = recommendationModelIds.calculatedDimension(`${column.id}.${grain}`);
    if (this.#exists(id)) return id;
    if (!CALENDAR_FUNCTIONS.has(grain)) {
      return this.#invalid(
        'unsupported',
        `El grano "${grain}" no tiene funcion de calendario en el AST de expresiones.`,
        column.id,
      );
    }
    this.#pendingIds.add(id);
    this.#operations.push({
      op: 'add',
      collection: 'calculatedDimensions',
      item: {
        id,
        name: `${levelName} (${column.name})`,
        tableId: ref.table.id,
        dataType: 'integer',
        role: 'temporal',
        expression: {
          type: 'function',
          name: grain as 'year' | 'quarter' | 'month' | 'week' | 'day',
          args: [{ type: 'column', columnId: column.id }],
        },
        metadata: this.#provenance,
      },
    });
    return id;
  }
}

/** Grano temporal de una expresion `date-trunc('<grano>', col)` o `year(col)`... */
function grainOf(expression: Expression): string | undefined {
  if (expression.type !== 'function') return undefined;
  if (expression.name === 'date-trunc') {
    const [grain] = expression.args;
    return grain?.type === 'literal' && typeof grain.value === 'string' ? grain.value : undefined;
  }
  return CALENDAR_FUNCTIONS.has(expression.name as TimeGrain) ? expression.name : undefined;
}
